import  { FormEvent, useEffect, useState } from 'react';
import { useBoolean } from '@fluentui/react-hooks';
import classNames from 'classnames';
import {
  ColumnActionsMode,
  DefaultButton,
  Dropdown,
  IColumn,
  IconButton,
  IDropdownOption,
  IIconProps,
  MessageBarType,
  Text,
} from '@fluentui/react';
import { useSelector } from 'react-redux';
import apiService from '../../../../../../api';
import useNotifications from '../../../../../../hooks/useNotifications';
import { customerSelector, locationSelector } from '../../../../../../redux/recordKeepingSlice';
import SeparatorGy from '../../../../../SeparatorGy/SeparatorGy';
import Pagination from '../../../../../Pagination/Pagination';

import { IPaginationProps } from '../../../../../Pagination/IPaginationProps';
import { ITireDispositionQueueState } from './ITireDispositionQueueState';
import { pageSizes } from '../../../../../../consts/recordKeeping';
import styles from './TireDispositionQueueTab.module.scss';
import { showHideQueueColumns } from './consts';
import Tooltip from '../../../../../../shared/Tooltip';
import LoadingScreen from '../../../../../LoadingScreen/LoadingScreen';
import { initialFilterState } from './FilterModalWindow/consts';
import FilterModalWindow from './FilterModalWindow/FilterModalWindow';
import { sortOrder } from '../../../../../../consts/sortOrder';
import GeneralModal from './GeneralModal/GeneralModal';
import moment from 'moment';
import PrintingModal from '../../../../../PrintingModal/PrintingModal';
import { downloadFile, printingTypes } from '../../../../../PrintingModal/consts';
import { ReconciliationData } from './ITireDispositionQueue';
import ErrorsModal from '../../../../../ErrorsModal/ErrorsModal';

const filterIcon : IIconProps = {iconName: 'Equalizer'};
const DATE_FORMAT = 'MM/DD/YYYY';
const STATUS_COMPLETED = 'Completed';
const STATUS_ERROR = 'Error';

const formatDate = (date: string) => date ? moment(date).format(DATE_FORMAT) : null;

const TireDispositionQueueTab = () => {

  const onColumnClick = (column: IColumn): void => {
    if (column.columnActionsMode == ColumnActionsMode.disabled) return;
    const newColumns: IColumn[] = [...columnsState];
    const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });
    setColumnsState(newColumns);
  };

  const [columnsState, setColumnsState] = useState<Array<any>>(showHideQueueColumns);
  const [showHideState, setShowHideState] = useState<Array<any>>(showHideQueueColumns);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filtersState, setFiltersState] = useState<any>(initialFilterState);
  const [showPrintExport, { toggle: toggleShowPrintExport }] = useBoolean(false);
  const { addNotification } = useNotifications();
  const { id: customerId } = useSelector(customerSelector);
  const { id: locationId } = useSelector(locationSelector);
  const [filtersData, setFiltersData] = useState<any>(null);
  const [advancedFilterData, setAdvancedFilterData] = useState<any>(null);
  const [payloadFilterData, setPayloadFilterData] = useState<any>(null);
  const [postTableId, setPostTableId] = useState<any>(null);
  const [showErrorsModal, { toggle: toggleShowErrorsModal }] = useBoolean(false);
  const [shouldOpenErrorsAfterGeneral, setShouldOpenErrorsAfterGeneral] = useState(false);

  const [state, setState] = useState<ITireDispositionQueueState>({
    items: [],
    selectedItems: [],
    loading: false,
    foundCount: 0,
  });

  const [paginationProps, setPaginationProps] = useState<IPaginationProps>({
    total: 0,
    current: 1,
    onChangePage: (newPage: number) => setPaginationProps((prev: any) => ({ ...prev, current: newPage })),
  });

  const [countOnPage, setCountOnPage] = useState<IDropdownOption>({ key: pageSizes[1], text: pageSizes[1].toString() });

  const [detailsVisible, { toggle: toggleDetailsVisible }] = useBoolean(false);
  const [tireOptixSurveyId, setTireOptixSurveyId] = useState<any>(null);
  const [glsSurveyId, setGlsSurveyId] = useState<any>(null);
  const [comments, setComments] = useState<any>(null);
  const [docNum, setDocNum] = useState<any>(null);
  const [dispCode, setDispCode] = useState<any>(null);
  const [transferTiresVisible, setTransferTiresVisible] = useState<any>(null);
  const [shipToCustomerId, setShipToCustomerId] = useState<any>(null);
  const [shipToLocId, setShipToLocId] = useState<any>(null);
  const [shipToCustomerName, setShipToCustomerName] = useState<any>(null);
  const [shipToLocCode, setShipToLocCode] = useState<any>(null);
  const [surveyDate, setSurveyDate] = useState<any>(null);
  const [plantNo, setPlantNo] = useState<any>(null);
  const [plantName, setPlantName] = useState<any>(null);


  const hideShowColumns = (column: any): any => {
    column.isVisible = !column.isVisible;
    const newColumns: any[] = [...columnsState].map((newColumn: any) => {
      if (newColumn.fieldName == column.fieldName) {
        newColumn.isVisible = column.isVisible;
      }
      return newColumn;
    });
    setShowHideState(newColumns);
  };

  const getSortOrder = () => {
    const { fieldName, isSortedDescending } = columnsState.find(({ isSorted }) => isSorted);
    return {
      column: fieldName,
      order: isSortedDescending ? sortOrder.DESC : sortOrder.ASC,
    };
  };

  const onChangeCountOnPageQueue = (event: FormEvent<HTMLDivElement>, item: IDropdownOption<any> | undefined): void => {
    setPaginationProps((prev: any) => ({ ...prev, current: 1 }));
    item && setCountOnPage(item);
  };

  const fetchTireOptixQueueRecords = async () => {
    setState(prev => ({...prev, loading: true}));
    try {
      const sortOrder = getSortOrder();
      const { data }: any = await apiService.tireDispositionQueue.get(
        { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
        sortOrder,
        customerId,
        locationId,
      );
      const foundCount = data.total.found;
      const items = data.data.map((item:any) => ({...item, 
        surveyDate: formatDate(item.surveyDate), 
        dateModified: formatDate(item.dateModified)}));
      setState((prev: any) => ({ ...prev, items, foundCount, selectedItems: [] }));
      setPaginationProps((prev: any) => ({ ...prev, total: Math.ceil(foundCount / +countOnPage.key) }));
      setFiltersData(null);
    } catch (e: any) {
      addNotification({
        text: `Tire Optix Queue fetching error: ${e?.message}`,
        type: MessageBarType.error,
      });
    } finally {
      setState((prev: any) => ({ ...prev, loading: false }));
    }
  };

  const fetchFilteredTireOptixQueueRecords = async () => {
    setState(prev => ({...prev, loading: true}));
    const filters = {
      dispositionCode: advancedFilterData.dispositionCode,
      queueStatus: advancedFilterData.queueStatus,
      surveyDateFrom: formatDate(advancedFilterData.surveyDateFrom),
      surveyDateTo: formatDate(advancedFilterData.surveyDateTo),
      dateModifiedFrom: formatDate(advancedFilterData.dateModifiedFrom),
      dateModifiedTo: formatDate(advancedFilterData.dateModifiedTo),
      surveyBy: advancedFilterData.surveyBy,
      lastModifiedBy: advancedFilterData.lastModifiedBy,
    };
    try {
      const sortOrder = getSortOrder();
      const { data }: any = await apiService.tireDispositionQueue.getFilters(
        { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
        sortOrder,
        customerId,
        locationId,
        filters,
      );
      const foundCount = data.total.found;
      const items = data.data.map((item:any) => ({...item, 
        surveyDate: formatDate(item.surveyDate), 
        dateModified: formatDate(item.dateModified)}));
      setState((prev: any) => ({ ...prev, items, foundCount, selectedItems: [] }));
      setPaginationProps((prev: any) => ({ ...prev, total: Math.ceil(foundCount / +countOnPage.key) }));
    } catch (e: any) {
      const { response } = e;
      addNotification({
        text: `Disposition Codes fetching error: ${response.data.message}`,
        type: MessageBarType.error,
      });
    } finally {
      setState((prev: any) => ({ ...prev, loading: false }));
    }
  };
  const handlePrint = async (printingType: any) => {
    setState(prev => ({ ...prev, loading: true }));
    toggleShowPrintExport();
    try {
      const sortOrder = getSortOrder();
      const queueColumns = showHideState;
      if (payloadFilterData) {
        const filters = {
          dispositionCode: payloadFilterData.dispositionCode,
          queueStatus: payloadFilterData.queueStatus ? payloadFilterData.queueStatus : null,
          surveyDateFrom: formatDate(payloadFilterData.surveyDateFrom),
          surveyDateTo: formatDate(payloadFilterData.surveyDateTo),
          dateModifiedFrom: formatDate(payloadFilterData.dateModifiedFrom),
          dateModifiedTo: formatDate(payloadFilterData.dateModifiedTo),
          surveyBy: payloadFilterData.surveyBy,
          lastModifiedBy: payloadFilterData.lastModifiedBy,
        };

        const requestData = {
          pagination: { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
          sortOrder,
          customerId,
          locationId,
          queueColumns,
          filters,
        };
        const { data }: any = printingType === printingTypes.excel ?
          await apiService.tireDispositionQueue.printExcel(requestData) :
          await apiService.tireDispositionQueue.printPdf(requestData);

        addNotification({
          text: 'File was successfully received.',
          type: MessageBarType.success,
        });
        downloadFile(data, printingType);
      }
      else {
        const requestData = {
          pagination: { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
          sortOrder,
          customerId,
          locationId,
          queueColumns,
        };
        const { data }: any = printingType === printingTypes.excel ?
          await apiService.tireDispositionQueue.printExcel(requestData) :
          await apiService.tireDispositionQueue.printPdf(requestData);

        addNotification({
          text: 'File was successfully received.',
          type: MessageBarType.success,
        });
        downloadFile(data, printingType);
      }

    } catch (e: any) {
      addNotification({
        text: 'Printing error',
        type: MessageBarType.error,
      });
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const isStatusComplete = (item: any) => {
    if (item.status === STATUS_COMPLETED)
      return true;
  };
  const isStatusError = (item: any) => {
    if (item.status === STATUS_ERROR)
      return true;
  };

  const handleFilter = async () => {
    if (advancedFilterData === null) {
      fetchTireOptixQueueRecords();
    } else {
      fetchFilteredTireOptixQueueRecords();
    }
  };

  const actionsClassName = (item: any) => {
    if (item.isSorted && item.isSortedDescending){
      return styles.descending;
    } else if (item.isSorted && !item.isSortedDescending){
      return styles.ascending;
    } else {
      return undefined;
    }
  };

  const handleClear = async () => {
    fetchTireOptixQueueRecords();
  };

  useEffect(() => {
    if (advancedFilterData === null) {
      fetchTireOptixQueueRecords();
    } else {
      fetchFilteredTireOptixQueueRecords();
    }
  }, [
    paginationProps.current,
    countOnPage,
    columnsState,
    customerId,
    locationId,
  ]);

  const openOOSReconciliationScreen = (
    data: ReconciliationData,
  ) => {
    const {
      toSurveyId,
      glsSurveyId,
      comments,
      docNum,
      dispoCode,
      shipToCustomerId,
      shipToLocId,
      shipToCustomerName,
      shipToLocCode,
      surveyDate,
      plantNo,
      plantName,
    } = data;
    setTireOptixSurveyId(toSurveyId);
    setGlsSurveyId(glsSurveyId);
    setComments(comments);
    setDocNum(docNum);
    setDispCode(dispoCode);
    setShipToCustomerId(shipToCustomerId);
    setShipToLocId(shipToLocId);
    setShipToCustomerName(shipToCustomerName);
    setShipToLocCode(shipToLocCode);
    setSurveyDate(surveyDate);
    setPlantNo(plantNo);
    setPlantName(plantName);

    if (dispoCode === 'TR') {
      setTransferTiresVisible(true);
    } else {
      setTransferTiresVisible(false);
    }
    toggleDetailsVisible();
  };

  const modalParams = {
    openOOSReconciliationScreen: (data: ReconciliationData) =>
      openOOSReconciliationScreen(data),
  };

  const searchRecordWithError = async (postTableId: any, glsSurveyId?: any) => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      setPostTableId(postTableId);
      // get the selected survey based on the glsSurveyId
      const selectedSurvey = state.items.find((item: any) => item.glsSurveyId === glsSurveyId);
      try {
        // open the modal using the data from selected survey
        modalParams.openOOSReconciliationScreen(
          {
            toSurveyId: selectedSurvey.tireOptixSurveyId,
            glsSurveyId: selectedSurvey.glsSurveyId,
            comments: selectedSurvey.comments,
            docNum: selectedSurvey.docNum,
            dispoCode: selectedSurvey.dispoCode,
            shipToCustomerId: selectedSurvey.shipToCustomerId,
            shipToLocId: selectedSurvey.shipToLocId,
            shipToCustomerName: selectedSurvey.shipToCustomerName,
            shipToLocCode: selectedSurvey.shipToLocCode,
            surveyDate: selectedSurvey.surveyDate,
            plantNo: selectedSurvey.plantNo,
            plantName: selectedSurvey.plantName,
          },
        );
        setShouldOpenErrorsAfterGeneral(true);
      } catch (e: any) {
        console.error(e);
        addNotification({
          text: 'Error: Unable to open Tire Details',
          type: MessageBarType.error,
        });
      }

    } catch (e: any) {
      const { response } = e;
      addNotification({
        text: `Errors fetching error: ${response?.data?.message}`,
        type: MessageBarType.error,
      });
    } finally {
      setState((prev: any) => ({ ...prev, loading: false }));
    }
  };

  const handleCloseDetailsModal = () => {
    toggleDetailsVisible(); // closes GeneralModal
    if (shouldOpenErrorsAfterGeneral) {
      setTimeout(() => {
        toggleShowErrorsModal(); // open ErrorsModal after closing
        setShouldOpenErrorsAfterGeneral(false);
      }, 0); // delay to let GeneralModal close first
    }
  };

  return (
    <>
      <div className='ms-Grid'>
        <div className='ms-Grid-row'>
          <div className='ms-Grid-col ms-sm12'>
            <div className={styles.filterBox}>
              <DefaultButton
                id="filterButton"
                className={classNames(styles.filterButton)}
                text="Filter"
                iconProps={filterIcon}
                onClick={() => setFilterVisible(true)}
              />
              <div className={classNames(styles.columnsList)}>
                {showHideQueueColumns.map((column: any, index: number) => (
                  <option key={column.name} id={column.name} className={column.isVisible ? styles.itemSelected : styles.itemNotSelected} onClick={() => hideShowColumns(column)} onKeyDown={() => hideShowColumns(column)}>{column.name}</option>
                ))}
              </div>
              <IconButton
                id="refreshButton"
                iconProps={{ iconName: 'Refresh' }} 
                onClick={fetchTireOptixQueueRecords} 
              />
            </div>
            <div className={styles.tableHeading}>
              <div>
                <Text variant="xLarge" className={styles.highlight}>Tire Optix Surveys</Text>
                <SeparatorGy vertical />
                <Text variant="xLarge" className={styles.highlight}>{state.foundCount} found</Text>
              </div>
              <div>
                <Text variant="large" className={styles.highlight}>Show # of rows:&nbsp;</Text>
                <Dropdown
                  options={pageSizes.map(pageSize => ({
                    key: pageSize,
                    text: pageSize.toString(),
                  }))}
                  defaultSelectedKey={pageSizes[0]}
                  selectedKey={countOnPage?.key}
                  onChange={onChangeCountOnPageQueue}
                />
              </div>
            </div>
          </div>
          <div className={styles['table-wrapper']}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Actions</th>
                  {
                    columnsState.map(item => item.isVisible && (
                      <th
                        key={item.key}
                        className={
                          actionsClassName(item)
                        }
                        onClick={() => onColumnClick(item)}
                      >
                        {item.name}
                        {item.tooltip ? <Tooltip 
                          content={item.tooltip}
                        />:null}
                      </th>
                    ))
                  }
                </tr>
              </thead>
              <tbody>
                {
                  state.items.map(item => (
                    <tr key={item.id}
                      className={
                        classNames(
                          styles.trBasic,
                          {
                            [styles.trSelected]: state.selectedItems.includes(item.id),
                            [styles.trSelectedError]: state.selectedItems.includes(item.id) && isStatusError(item),
                          },
                          {
                            [styles.trError]: isStatusError(item),
                          },
                          {
                            [styles.trComplete]: isStatusComplete(item),
                          },
                        )
                      }
                    >
                      {(isStatusComplete(item)) ? <td><IconButton
                        id="viewButton"
                        iconProps={{ iconName: 'View' }}
                        onClick={()=>modalParams.openOOSReconciliationScreen({
                          toSurveyId: item.tireOptixSurveyId,
                          glsSurveyId: item.glsSurveyId,
                          comments: item.comments,
                          docNum: item.docNum,
                          dispoCode: item.dispoCode,
                          shipToCustomerId: item.shipToCustomerId,
                          shipToLocId: item.shipToLocId,
                          shipToCustomerName: item.shipToCustomerName,
                          shipToLocCode: item.shipToLocCode,
                          surveyDate: item.surveyDate,
                        })}
                      /></td> : <td><IconButton
                        id="editButton"
                        iconProps={{ iconName: 'Edit' }}
                        onClick={()=>modalParams.openOOSReconciliationScreen({
                          toSurveyId: item.tireOptixSurveyId,
                          glsSurveyId: item.glsSurveyId,
                          comments: item.comments,
                          docNum: item.docNum,
                          dispoCode: item.dispoCode,
                          shipToCustomerId: item.shipToCustomerId,
                          shipToLocId: item.shipToLocId,
                          shipToCustomerName: item.shipToCustomerName,
                          shipToLocCode: item.shipToLocCode,
                          surveyDate: item.surveyDate,
                          plantNo: item.plantNo,
                          plantName: item.plantName,
                        })}
                      /></td>}
                      {
                        columnsState.map(column => column.isVisible && (
                          <td key={item.id}>{item[column.fieldName]}</td>
                        ))
                      }
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
          <SeparatorGy />
          <Pagination {...paginationProps} />
        </div>
        <div className={styles.detailsRow}>
          <div className={styles.detailsColumn}>
            <div className={styles.fieldsActions}>
              <DefaultButton
                onClick={toggleShowErrorsModal}
                text="View Errors"
              />
              <DefaultButton
                id="printButton"
                text="Export"
                onClick={toggleShowPrintExport}
              />
            </div>
          </div>
        </div>
      </div>
      {filterVisible && <FilterModalWindow isOpened={setFilterVisible} filtersState={filtersState} setFiltersState={setFiltersState} setFiltersData={setFiltersData} filtersData={filtersData} handleFilter={handleFilter} handleClear={handleClear} setAdvancedFilterData={setAdvancedFilterData} advancedFilterData={advancedFilterData} setPayloadFilterData={setPayloadFilterData} payloadFilterData={payloadFilterData}/>}
      {detailsVisible && <GeneralModal 
        isOpened={detailsVisible} 
        isTransferTire={transferTiresVisible} 
        onDismiss={handleCloseDetailsModal} 
        surveyDetails={{
          docNum,
          tireOptixSurveyId,
          glsSurveyId,
          comments,
          dispCode,
          surveyDate,
          plantNo,
          plantName,
        }}
        fetchQueueRecords={fetchTireOptixQueueRecords} 
        shippingDetails={{
          shipToCustomerName,
          shipToLocCode,
          shipToCustomerId,
          shipToLocId,
        }}
        tireDetails={{
          tireId: postTableId,
        }}
      />}
      {state.loading && <LoadingScreen />}
      <PrintingModal
        isOpened={showPrintExport}
        onClose={toggleShowPrintExport}
        onPrint={handlePrint}
      />
      <ErrorsModal
        searchError={searchRecordWithError}
        isModalOpen={showErrorsModal}
        hideModal={toggleShowErrorsModal}
        fetchErrorsService={apiService.tireDispositionQueueTires.error}
        isQueue={true}
        shouldConfirm
      />
    </>
  );
};

export default TireDispositionQueueTab;